import { useState, useMemo, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { FiltersDrawer, type FiltersState } from './FiltersDrawer';
import { useAppointmentCalendarSettings } from './useAppointmentCalendarSettings';
import { getWeekdayLabels, buildMonthGrid } from './calendarHelpers';
import {
  useAppointmentsData,
  appointmentToPrefill,
  type EnrichedAppointment,
} from './useAppointmentsData';

// ─── Helpers ───

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function isToday(year: number, month: number, date: number): boolean {
  const now = new Date();
  return now.getFullYear() === year && now.getMonth() === month && now.getDate() === date;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function toDateStr(year: number, month: number, day: number): string {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

// ─── Filter chip ───

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-1)',
        height: 28,
        padding: '0 var(--sp-2) 0 var(--sp-3)',
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-primary-bg, rgba(59,130,246,0.08))',
        color: 'var(--color-primary)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        style={{
          background: 'none',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          padding: '0 2px',
          fontSize: 'var(--font-size-base)',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        ×
      </button>
    </span>
  );
}

// ─── Appointment Pill ───

function AppointmentPill({
  appointment,
  onClick,
}: {
  appointment: EnrichedAppointment;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-1)',
        padding: '2px 6px',
        borderRadius: 'var(--radius-xs)',
        background: appointment.color,
        fontSize: 11,
        fontWeight: 'var(--font-weight-medium)',
        color: '#1e293b',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
        lineHeight: '18px',
      }}
    >
      <span style={{ opacity: 0.7 }}>{appointment.startTime}</span> {appointment.patientName}
    </div>
  );
}

// ─── Month View Page ───

const DEFAULT_FILTERS: FiltersState = {
  quickFilter: 'this-month',
  location: '',
  provider: '',
  category: '',
  status: '',
  service: '',
  room: '',
};

export function MonthViewPage() {
  const brandTokens = useBrandTokens();
  const { openBookDrawer } = useAppointmentsContext();
  const calSettings = useAppointmentCalendarSettings();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS);

  // Compute date range for the full month
  const startDate = useMemo(() => toDateStr(year, month, 1), [year, month]);
  const endDate = useMemo(() => {
    const lastDay = new Date(year, month + 1, 0).getDate();
    return toDateStr(year, month, lastDay);
  }, [year, month]);

  const { appointments, lookups } = useAppointmentsData(
    startDate,
    endDate,
    calSettings.colorAppointmentsBy,
  );

  // Apply filters
  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !a.patientName.toLowerCase().includes(q) &&
          !a.providerName.toLowerCase().includes(q) &&
          !a.serviceName.toLowerCase().includes(q)
        )
          return false;
      }
      if (filters.provider && a.providerId !== filters.provider) return false;
      if (filters.category && a.categoryId !== filters.category) return false;
      if (filters.status && a.statusId !== filters.status) return false;
      if (filters.service && a.serviceId !== filters.service) return false;
      return true;
    });
  }, [appointments, search, filters]);

  // Group appointments by date string
  const appointmentsByDate = useMemo(() => {
    const map = new Map<string, EnrichedAppointment[]>();
    for (const appt of filtered) {
      const key = appt.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(appt);
    }
    return map;
  }, [filtered]);

  const weekdayLabels = useMemo(
    () => getWeekdayLabels(calSettings.weekStartsOn),
    [calSettings.weekStartsOn],
  );

  const grid = useMemo(() => {
    const cells = buildMonthGrid(year, month, calSettings.weekStartsOn);
    return cells.map((cell) => {
      let cellYear = year;
      let cellMonth = month;
      if (cell.month === 'prev') {
        cellMonth = month - 1;
        if (cellMonth < 0) {
          cellMonth = 11;
          cellYear = year - 1;
        }
      } else if (cell.month === 'next') {
        cellMonth = month + 1;
        if (cellMonth > 11) {
          cellMonth = 0;
          cellYear = year + 1;
        }
      }
      const dateStr = toDateStr(cellYear, cellMonth, cell.date);
      return {
        ...cell,
        appointments: cell.month === 'current' ? (appointmentsByDate.get(dateStr) ?? []) : [],
      };
    });
  }, [year, month, calSettings.weekStartsOn, appointmentsByDate]);

  const handlePrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const handleToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const handleRemoveQuickFilter = () => {
    setFilters((prev) => ({ ...prev, quickFilter: '' }));
  };

  const quickFilterLabel =
    filters.quickFilter === 'today'
      ? 'Today'
      : filters.quickFilter === 'this-week'
        ? 'This Week'
        : filters.quickFilter === 'this-month'
          ? 'This Month'
          : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl2)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl2)',
              letterSpacing: '-0.44px',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Appointments
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 0',
            }}
          >
            View and manage clinic appointments
          </p>
        </div>

        <Button variant="gradient" size="md" onClick={() => openBookDrawer()}>
          + Book a Patient
        </Button>
      </div>

      {/* Toolbar: Month Nav + Filters */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {/* Month Navigation */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
          }}
        >
          <button type="button" onClick={handlePrevMonth} style={navBtnStyle}>
            <NavIcon name="chevron-left" />
          </button>
          <span
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              minWidth: 180,
              textAlign: 'center',
            }}
          >
            {MONTH_NAMES[month]} {year}
          </span>
          <button type="button" onClick={handleNextMonth} style={navBtnStyle}>
            <NavIcon name="chevron-right" />
          </button>
        </div>

        {/* Today button */}
        <button type="button" onClick={handleToday} style={todayBtnStyle}>
          Today
        </button>

        {/* Active filter chip */}
        {quickFilterLabel && (
          <FilterChip label={quickFilterLabel} onRemove={handleRemoveQuickFilter} />
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search appointments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: 220,
              height: 36,
              padding: '0 var(--sp-4) 0 40px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </div>

        {/* Filters button */}
        <button
          type="button"
          onClick={() => setFiltersOpen(true)}
          style={{
            height: 36,
            padding: '0 var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
          }}
        >
          <NavIcon name="sliders" />
          Filters
        </button>
      </div>

      {/* Calendar Grid */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--sp-4) var(--sp-6) var(--sp-6)' }}>
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          {/* Single unified grid: header row + 6 day rows */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gridTemplateRows: 'auto repeat(6, 1fr)',
            }}
          >
            {/* Weekday headers */}
            {weekdayLabels.map((day, idx) => {
              const isWeekendHeader = day === 'Sun' || day === 'Sat';
              return (
                <div
                  key={day}
                  style={{
                    padding: 'var(--sp-3) var(--sp-2)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'center',
                    borderBottom: '1px solid var(--border)',
                    borderRight: idx < 6 ? '1px solid var(--border)' : undefined,
                    background: isWeekendHeader ? 'rgba(0,0,0,0.02)' : 'var(--bg-surface)',
                  }}
                >
                  {day}
                </div>
              );
            })}

            {/* Day cells */}
            {grid.map((cell, i) => {
              const today = cell.month === 'current' && isToday(year, month, cell.date);
              const muted = cell.month !== 'current';
              const col = i % 7;
              const row = Math.floor(i / 7);
              const isWeekend = weekdayLabels[col] === 'Sun' || weekdayLabels[col] === 'Sat';

              // Weekend columns get a subtle tint; overflow days stay white with gray text
              let bg = 'transparent';
              if (isWeekend) {
                bg = 'rgba(0,0,0,0.02)';
              }

              // Compute full date string for this cell
              let cellYear = year;
              let cellMonth = month;
              if (cell.month === 'prev') {
                cellMonth = month - 1;
                if (cellMonth < 0) {
                  cellMonth = 11;
                  cellYear = year - 1;
                }
              } else if (cell.month === 'next') {
                cellMonth = month + 1;
                if (cellMonth > 11) {
                  cellMonth = 0;
                  cellYear = year + 1;
                }
              }
              const cellDateStr = toDateStr(cellYear, cellMonth, cell.date);

              return (
                <div
                  key={i}
                  onClick={() => openBookDrawer({ date: cellDateStr })}
                  style={{
                    minHeight: 100,
                    padding: 'var(--sp-2)',
                    borderBottom: row < 5 ? '1px solid var(--border)' : undefined,
                    borderRight: col < 6 ? '1px solid var(--border)' : undefined,
                    background: bg,
                    cursor: 'pointer',
                  }}
                >
                  {/* Day number */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      padding: '2px 2px',
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        width: today ? 28 : undefined,
                        height: today ? 28 : undefined,
                        borderRadius: today ? 'var(--radius-full)' : undefined,
                        background: today ? 'var(--color-primary)' : undefined,
                        color: today ? '#ffffff' : muted ? 'var(--text-muted)' : 'var(--text)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: today ? 'var(--font-weight-bold)' : 'var(--font-weight-medium)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {cell.date}
                    </span>
                  </div>

                  {/* Appointment pills */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    {cell.appointments.slice(0, 3).map((appt) => (
                      <AppointmentPill
                        key={appt.id}
                        appointment={appt}
                        onClick={() => {
                          openBookDrawer(appointmentToPrefill(appt));
                        }}
                      />
                    ))}
                    {cell.appointments.length > 3 && (
                      <span
                        style={{
                          fontSize: 10,
                          color: 'var(--text-muted)',
                          padding: '0 6px',
                          cursor: 'pointer',
                        }}
                      >
                        +{cell.appointments.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters Drawer */}
      <FiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        brandTokens={brandTokens}
        filters={filters}
        onApply={setFilters}
        visibleFilters={calSettings.filters}
        lookups={lookups}
      />
    </div>
  );
}

// ─── Shared styles ───

const navBtnStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--text)',
  padding: 0,
};

const todayBtnStyle: CSSProperties = {
  height: 32,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  cursor: 'pointer',
};
